import $ from 'jquery'
import _ from 'lodash'
import Backbone from 'backbone'
import KeyCodes from 'scoreboard/components/key-codes'
import Tooltip from 'scoreboard/components/tooltip'
import selects from 'scoreboard/components/selects'
import deviceType from 'scoreboard/components/device-type'
import equationToolbar from './equation-toolbar.hbs'
import FormView from 'scoreboard/components/form-view'
import equationHighlighter from 'scoreboard/components/equations/equation-highlighter'
import formHelper from 'scoreboard/components/form-helper'
import dropdown from 'scoreboard/components/dropdown'
import i18n from 'scoreboard/components/i18n'
import constants from 'scoreboard/components/constants'

function setCursor(element, position) {
	if (!element || !position) {
		return
	}

	const selection = window.getSelection()
	// #35798 if the user builds an equation without ever clicking in the input box,
	// Safari will return a Selection object with a None type.
	if (selection.type === 'None') {
		return
	}

	var range = selection.getRangeAt(0)
	if (range.collapsed) {
		range.deleteContents()
		range.setStart(element, position)
		range.setEnd(element, position)
		selection.removeAllRanges()
		selection.addRange(range)
	}
}

//list of supported functions from JEP
//see http://www.singularsys.com/jep/doc/html/functions.html
var functions = [ 'sin', 'cos', 'tan', 'asin', 'acos', 'atan', 'atan2', 'sec',
	'cosec', 'cot', 'sinh', 'cosh', 'tanh', 'asinh', 'acosh', 'atanh', 'ln',
	'log', 'lg', 'exp', 'pow', 'round', 'rint', 'floor', 'ceil',
	'if', 'abs', 'rand', 'mod', 'sqrt', 'binom', 'signum' ]

var EquationEditor = Backbone.View.extend({

	/**
	 * Common functionality for editing equations that's shared by
	 * scorecards/edit/equation-form.js, datasets/edit/fields/field-step2.js, etc.
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {String} [options.equation] an equation to be edited
	 *
	 * @param {EquationParser} options.equationParser
	 *
	 * @param {Object} options.equationFormatter
	 *
	 * @param {Boolean} [options.required=true] whether the equation is required
	 *
	 * @param {Boolean} [options.allowFunctionTooltip=true] whether the function
	 * tooltip should be displayed after selecting a range of text (for
	 * functions like sum, avg, etc)
	 *
	 * @param {Function|boolean} [options.allowCleanSubmission=false]
	 * when you want to allow clicking next
	 * even if there are no changes in the form (e.g. maybe you're creating
	 * something new and all the defaults are OK, or you're in a wizard where
	 * all the fields are optional).
	 * Can be truthy or a function that returns a truthy value
	 *
	 * @param {Function} [test] the test function that formView will call. This must
	 * be passed in as an option, because it has .bind called on it in the constructor,
	 * so anything that changes this.test after the fact won't get called by formView
	 */
	initialize: function(options = {}) {
		this.options = options || {}
		this.equationParser = options.equationParser
		this.equationFormatter = options.equationFormatter
		// used in determining whether to process keyup events
		this.keysLocked = true
		const test = this.options.test || this.test.bind(this)
		let TheFormView = FormView.extend({
			test
		})
		this._formView = new TheFormView({
			el: this.el,
			submit: this.submit.bind(this),
			allowCleanSubmission: options.allowCleanSubmission,
			guard: false
		})
		this.allowFunctionTooltip = _.isNil(options.allowFunctionTooltip) ? true : options.allowFunctionTooltip
		this.required = _.isNil(options.required) ? true : options.required
	},

	events: {
		// HTML5 event would be oninput, but IE doesn't support it.
		// Using keyup here because keydown/keypress are fired before the .equation
		// contenteditable is updated with the newly entered keystroke #27369.
		'keyup .equation': 'typeEquation',
		// click events don't capture selecting a range of text in FF, so use
		// mouseup and touchend instead.
		'mouseup .equation': 'clickEquation',
		'touchend .equation': 'clickEquation',
		'blur .equation': 'removeSelected',
		'click .add': 'add',
		// Anytime a select value changes, the table could potentially change sizes,
		// so we need to recaulate the max-width of the dialog accordingly
		'change select': 'referenceChange',
		'keyup input': 'referenceChange',
		// If this view was loaded while a key was already pressed, the releasing
		// of that key will trigger events prematurely (#25580). This keydown
		// changes the keysLocked flag which ensures any subsequent keyup events
		// indeed come from this view and aren't ignored.
		'keydown .equation, input': 'unlockKeypresses'
	},

	unlockKeypresses: function(e) {
		this.keysLocked = false
		if (e.keyCode == KeyCodes.ENTER) {
			// don't try to go to the next line in the input box
			e.preventDefault()
			this.trigger('submit')
			return
		}
	},

	render: function() {
		this.selection()
		this.renderEquation(this.options.equation)
		// selection can't be set until after the form is in the DOM
		process.nextTick(this.selection.bind(this))
		this._formView.start()
		this.selection()
		return this
	},

	/**
	 * Disables cut/paste at all times, and displays the given error to the user
	 */
	disableCutPaste: function(errorMsg = 'datasets.equation.invalidCutPaste') {
		this.registerCutPasteValidator(() => i18n.get(errorMsg))
	},

	/**
	 * Registers a validator that selectively allows for cut and paste
	 * operations. If the validator returns a String, that is displayed to the
	 * user as an error message. If it returns nil, the cut/paste is allowed
	 */
	registerCutPasteValidator: function(validator) {
		// https://www.codexworld.com/disable-mouse-right-click-cut-copy-paste-using-jquery/
		this.$('[contenteditable].equation').bind('cut paste', e => {
			let previousEquation = this.getEquation()
			// let the cut or paste go through
			process.nextTick(() => {
				// after the equation has been changed, revert the change
				let error = validator()
				if (error) {
					this._formView.errorMessage(i18n.get(error))
					if (_.isEmpty(previousEquation)) {
						this.$('.equation').empty()
					} else {
						this.renderEquation(previousEquation)
					}
				}
			})
		})
	},

	// handle user input in equation box
	typeEquation: function(e) {
		if (this.keysLocked) {
			return
		}

		// record the old cursor position so we can restore it.
		this.recordCursorPosition()

		// if the equation didn't change, something like pasting in an equation could
		// have happened, so make sure the right stuff is selected
		if (this.previousEquation === this.$('.equation').text()) {
			this.selection()
			return
		}

		// render the equation again
		this.renderEquation()

		// restore the text node that corresponds to the new cursor position
		this.recordCursorEl()
		setCursor(this.cursorEl[0], this.cursorElOffset)

		// highlight the appropriate reference in the equation input and
		// table
		this.selection()
	},

	/**
	 * handle clicks in the equation input box
	 * @param triggerEdit whether to trigger the edit function after the selection
	 * is made
	 */
	clickEquation: function(triggerEdit = true) {
		// if the drag started or ended outside the equation box, ignore it
		if (this.$(getSelection().anchorNode).closest('.equation').length == 0 ||
			this.$(getSelection().focusNode).closest('.equation').length == 0) {
			return
		}

		this.recordCursorPosition()
		this.recordCursorEl()
		this.selection(triggerEdit)
	},

	// record the cursor position within the equation input box as
	// this.cursorPosition
	recordCursorPosition: function() {
		var selection = getSelection()
		// if a range of stuff is chosen we didn't just type, we did something
		// funny like stop pressing the shift key and we should just not do the stuff below
		if (selection.anchorNode != selection.focusNode) {
			return
		}
		var focusNode = $(selection.focusNode)
		var reference = focusNode.parent().hasClass('reference')
		// the node used to calculate an index on the next line
		var indexNode = reference ? focusNode.parent() : focusNode
		var index = this.$('.equation').contents().index(indexNode)
		var equationContents = this.$('.equation').contents()
		this.cursorPosition = equationContents.slice(0, index).text().length + selection.focusOffset
	},

	// record the text node that corresponds to the cursor position in
	// this.cursorEl, and also record the offset of the cursor within that
	// element in this.cursorElOffset
	recordCursorEl: function() {
		// index within equationContents
		var contentIndex = 0
		// remember how many more characters forward the cursor's position
		// should be
		this.cursorElOffset = this.cursorPosition
		var equationContents = this.$('.equation').contents()
		// advance to the right spot in the equationContents
		while (equationContents.eq(contentIndex).text().length < this.cursorElOffset &&
				contentIndex <= equationContents.length) {
			this.cursorElOffset -= equationContents.eq(contentIndex).text().length
			contentIndex++
		}
		// grab the element which contains the cursor
		var element = equationContents.eq(contentIndex)
		var reference = element.hasClass('reference')
		this.cursorEl = reference ? element.contents().first() : element
	},

	// handle changes to the selection in the equation box
	selection: function(triggerEdit = true) {
		this.removeSelected()
		this._selection = this.getSelection()
		const anchorAndFocusDiffers = this._selection.anchorNode != this._selection.focusNode ||
				this._selection.anchorOffset != this._selection.focusOffset
		// if a range of text was selected
		// use window.getSelection() here since this._selection isn't a Selection object
		if (!_.isEmpty(getSelection().toString()) && anchorAndFocusDiffers) {
			// anchorNode is the node in which the selection begins
			var attachTo = this.$(this._selection.anchorNode)
			if (this._selection.anchorNode.nodeType == Node.TEXT_NODE &&
				attachTo.parent()[0] !== this.$('.equation')[0]) {
				attachTo = attachTo.parent()
			}
			// if there's already a tooltip
			if (this._functionsTooltip && !this._functionsTooltip.removed &&
				this._functionsTooltip.attachedTo()[0] === attachTo[0]) {
				return
			}
			// issue #9912 if no valid selection
			// issue #9914 highlighting the add button shouldn't work either
			if (_.isEmpty(attachTo) || attachTo.is('button')) {
				// don't show the tooltip and don't highlight anything
				return
			}

			if (this.allowFunctionTooltip) {
				_.invokeMap([this._functionsTooltip], 'remove')
				this._functionsTooltip = new Tooltip({
					attachTo: attachTo,
					content: this.toolbar(),
					placement: 'above',
					arrow: 'center',
					extraClass: 'equation-toolbar'
				}).render()
				this._functionsTooltip.delegateEvents({
					'click button': this.selectFunction.bind(this),
					'change select': this.selectFunction.bind(this)
				})
			}

			// don't highlight anything
			return
		} else {
			_.invokeMap([this._functionsTooltip], 'remove')
		}

		// if we reach here, a range of text was NOT chosen, so highlight the
		// appropriate reference in the equation if that's the one the user is
		// focusing on.  also update the form controls to match the highlighted
		// referenced
		const parent = this.getSelectionParent()
		this.updateMatchIndex(parent, false)
		this.trigger('selection')
		if (triggerEdit && parent.hasClass('reference')) {
			this.edit(this._matches[parent.index()], this._matches, parent.index())
		}
		this.referenceChange()
	},

	/**
	 * Broken out so can be mocked during testing without messing with Window.getSelection()
	 * @returns {*|jQuery}
	 */
	getSelectionParent: function() {
		return $(getSelection().focusNode).parent()
	},

	updateMatchIndexForAddedReference: function() {
		// if there's no cursor position in the editor, then we can select the
		// last reference in the equation, since that's the one that was just added
		if (_.isNil(this.cursorPosition)) {
			this.updateMatchIndexToLastReference()
			return
		}

		const addedMatch = _.find(this._matches, match =>
			match.index === this.cursorPosition)
		// if we didn't add a match, then we're not editing a reference
		if (_.isNil(addedMatch)) {
			this._matchIndex = null
			return
		}

		this._matchIndex = _.indexOf(this._matches, addedMatch)
		const matchEl = this.$('.reference').eq(this._matchIndex)
		this.updateMatchIndex(matchEl)
	},

	updateMatchIndexToLastReference: function() {
		this.updateMatchIndex(this.$('.reference').last())
	},

	updateMatchIndex: function(el, triggerEdit = true) {
		if (el?.hasClass('reference')) {
			this._updateMatchIndexForReference(el, triggerEdit)
		} else {
			this._matchIndex = null
			// show the add button since we're not editing an existing reference
			this.toggleAddVisible(true)
		}
	},

	/**
	 * This is broken out for testing, because overriding window.getSelection is
	 * more complicated than just mocking using this.
	 */
	_updateMatchIndexForReference: function(el, triggerEdit = true) {
		// must be before this.edit in case this.edit needs to know the index
		this._matchIndex = el.index()
		// highlight inside the input area
		el.addClass('is-selected')

		// in certain cases like #selection() we want to update the _matchIndex without
		// calling the edit function
		if (triggerEdit) {
			this._editForUpdatedMatchIndex(el)
		}
	},

	_editForUpdatedMatchIndex: function(el) {
		this.edit(this._matches[el.index()], this._matches, el.index())
	},

	// add the appropriate function to the equation
	selectFunction: function(e) {
		// this should refer to the EquationForm view
		var fun = $(e.target).val()
		var first, last, firstOffset, lastOffset
		if (this._selection.anchorNode.compareDocumentPosition(this._selection.focusNode)
			    & Node.DOCUMENT_POSITION_PRECEDING) {
			first = this.$(this._selection.focusNode)
			last = this.$(this._selection.anchorNode)
			firstOffset = this._selection.focusOffset
			lastOffset = this._selection.anchorOffset
		} else {
			first = this.$(this._selection.anchorNode)
			last = this.$(this._selection.focusNode)
			firstOffset = this._selection.anchorOffset
			lastOffset = this._selection.focusOffset
		}

		// add the function ...

		// if we're working inside just one node
		if (this._selection.anchorNode == this._selection.focusNode) {
			var replacement =
				fun + '(' +
				first.text().substring(firstOffset, lastOffset) +
				')'
			if (first[0].nodeType == Node.TEXT_NODE) {
				if (firstOffset > lastOffset) {
					var temp = firstOffset
					firstOffset = lastOffset
					lastOffset = temp
				}
				replacement =
					first.text().substring(0, firstOffset) +
					replacement +
					last.text().substring(lastOffset)
			}
			first.replaceWith(replacement)
		// if we have to work with two nodes
		} else {
			first.replaceWith(
				first.text().substring(0, firstOffset) +
				fun + '(' +
				first.text().substring(firstOffset))
			last.replaceWith(
				last.text().substring(0, lastOffset) +
				')' +
				last.text().substring(lastOffset))
		}

		this._functionsTooltip.remove()

		// render the equation again
		this.renderEquation()

		// the selection is all messed up after messing with the text above
		// so just clear the selection for now.  we could try to restore it if
		// that turns out to be a usability issue (the trouble is it's a big
		// pain to do so)
		getSelection().removeAllRanges()
	},

	toolbar: function() {
		if (!this._toolbar) {
			this._toolbar = $(equationToolbar())
			functions.sort()
			dropdown.values(this._toolbar.find('.functions'), functions)
		}
		selects(this._toolbar.find('select'))
		return this._toolbar
	},

	removeSelected: function() {
		// remove highlight from input area and all table rows
		this.$('tbody, .equation').children().removeClass('is-selected')
	},

	// add a new function reference to the equation, based on the settings in
	// the gui controls
	add: function(e) {
		var data = this._referenceData(this.$('.equation-controls'))
		var formatted = this.equationFormatter.format(data)
		let equation = this.$('.equation').text()

		// We have a cursorPosition, insert the new value at that position
		if (this.cursorPosition) {
			let head = equation.slice(0, this.cursorPosition)
			let tail = equation.slice(this.cursorPosition)
			this.$('.equation').text(head + formatted + tail)
		// There's no cursorPosition, tack the value at the end of the existing
		// equation
		} else {
			this.$('.equation').text(equation + formatted + ' ')
		}

		this.renderEquation()
		this._formView.change(e)
		this.updateMatchIndexForAddedReference()

		// Safari's cursor position breaks after adding a new reference, so
		// reset it here to be at the tail end of the most recently added reference
		if (deviceType.safari) {
			let index = this.cursorPosition || 0
			setCursor(this.$(`.reference[data-index=${index}]`)[0], 1)
		}
	},

	/**
	 * Initializes the equation editor form GUI controls for editing the
	 * selected reference, e.g. M(1)
	 *
	 * @param match the current match
	 * @param matches all the matches in the equation
	 * @param matchIndex the index of the match within the matches
	 */
	edit: function(match, matches, matchIndex) {
		this.toggleAddVisible(false)
		this.trigger('edit', match, matches, matchIndex)
	},

	// toggles the add button
	// addVisible indicates whether the add button should be displayed
	toggleAddVisible: function(addVisible) {
		this.$('.add-control').toggleClass('is-not-displayed', !addVisible)
		this.isEditingReference = !addVisible
	},

	renderEquation: function(equationExpression) {
		// re-calculate the matches for the current equation
		var equationExpression = equationExpression || this.$('.equation').text()
		this._matches = this.getMatches(equationExpression)
		// IE11 renders the references strangely, and Edge doesn't work well
		// when you try to add a function like max() around a reference
		var highlight = !deviceType.trident && !deviceType.edge
		var { equation, hasReference } = equationHighlighter.highlightTerms(
			equationExpression, this._matches, highlight)

		// render the equation input area
		this.$('.equation').empty().append(equation)
		this.previousEquation = equationExpression

		// If references appear (.badge(s) within the equation input), mark the
		// input so that the styling can change to handle them.
		this.$('.equation').toggleClass('has-references', hasReference == true)

		this.trigger('render:equation')
	},

	/**
	 * Returns the currently rendered equation.  Note the previous equation is
	 * available as this.previousEquation
	 */
	getEquation: function() {
		return this.$('.equation').text()
	},

	/**
	 * Returns the matches found by parsing the equation
	 */
	getMatches: function(equationExpression) {
		equationExpression = equationExpression || this.$('.equation').text()
		return this.equationParser.parse(equationExpression)
	},

	// extracts the data for a reference from within the given element
	_referenceData: function(el) {
		return formHelper.readFromDom(el)
	},

	// when done editing a reference
	referenceDone: function(e) {
		var row = this.$('.equation-controls')
		var data = this._referenceData(row)

		this.previousEquation = this.$('.equation').text()

		// replace text in the equation input box
		var newReference = this.equationFormatter.format(data)
		if (!_.isNil(this._matchIndex) && !_.isNil(newReference)) {
			this.$('.equation .reference').eq(this._matchIndex)
				.removeClass('is-invalid')
				.text(newReference)
		}

		// #13379 update the matches with the new reference
		this._matches = this.equationParser.parse(this.$('.equation').text())

		this._formView.change(e)
	},

	// the selection can change or go away (for example if you click on
	// the Other dropdown to pick a weird function like cos).  We need to
	// keep a copy of the selection, but _.clone doesn't work for some reason
	// So, just keep the properties we're interested in
	getSelection: function() {
		const fields = ['anchorNode', 'anchorOffset', 'focusNode', 'focusOffset']
		return _.pick(getSelection(), fields)
	},

	/**
	 * Returns the equation with all funky characters removed (non-breaking
	 * space, curly quotes, etc)
	 */
	getCleanEquation: function() {
		var equation = this.$('.equation').text()
		// get rid of non-breaking spaces (&nbsp;)
		equation = equation.replace(/\u00A0/g, ' ')
		// get rid of curly quotes
		equation = equation.replace(/[\u201C\u201D]/g, '"')
		return equation
	},

	test: function() {
		if (this.required && _.isEmpty(_.trim(this.$('.equation').text()))) {
			return i18n.get('common.equation.required')
		}
	},

	submit: function() {
		this.trigger('submit')
	},

	referenceChange: function(referenceChange) {
		if (this.keysLocked && !referenceChange) {
			return
		}

		// If editing an existing reference, auto adust it as selections are made.
		if (referenceChange && this.isEditingReference) {
			this.referenceDone()
		}

		// Because the table width changes depending on which controls are showing,
		// We need to set a new max-width each time the table width changes.
		// This will cause the badges to wrap when they get to the end of the input
		// rather than increasing the width of the dialog.
		// Wait till after the controls have been switched/resized to do the
		// dialog measuring/resizing
		process.nextTick(function() {
			var tableWidth = this.$('.table').width()
			let dialogWidth = tableWidth > constants.EQUATION_EDITOR_MIN_WIDTH ?
				tableWidth : constants.EQUATION_EDITOR_MIN_WIDTH
			this.$('.dialog-body-content').css('max-width', (dialogWidth + 44) + 'px')
		}.bind(this))
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this._functionsTooltip, this._formView], 'remove')
	}

})

export default EquationEditor

EquationEditor.__filename__ = 'components/equations/equation-editor.js'
